import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Field, Select } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Overlay'
import { countLabel } from '@/lib/format'
import type { Segment } from '@/types'

/**
 * Deleting a segment never cascade-deletes its programs; it forces a
 * reassignment choice instead (docs/03-page-specs.md §5.1). Blocking the
 * delete while the segment is non-empty is the wrong call — reassign-on-delete
 * is what a reviewer actually wants.
 */
export function DeleteSegmentModal({
  segment,
  segments,
  affected,
  busy = false,
  onClose,
  onConfirm,
}: {
  segment: Segment | null
  segments: Segment[]
  affected: number
  busy?: boolean
  onClose: () => void
  onConfirm: (reassignTo: string | null) => void
}) {
  const [target, setTarget] = useState('')

  if (!segment) return null
  const others = segments.filter((s) => s.id !== segment.id)

  return (
    <Modal
      open
      onClose={onClose}
      title={`Delete “${segment.name}”?`}
      description={
        affected === 0
          ? 'No programs are assigned to this segment.'
          : `${countLabel(affected, 'program')} assigned to it. Choose where they should go — deleting a segment never deletes its programs.`
      }
      footer={
        <>
          <Button size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" variant="danger" loading={busy} onClick={() => onConfirm(target || null)}>
            Delete segment
          </Button>
        </>
      }
    >
      {affected > 0 && (
        <Field label="Move those programs to">
          {(p) => (
            <Select {...p} value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">Unassigned</option>
              {others.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
    </Modal>
  )
}
